import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Download, Save, Loader2, Mail, Users, TrendingUp, Calendar } from "lucide-react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const SOURCES = ["popup", "footer", "sidebar", "tool_report", "homepage", "contact"];
const COLORS = ["#39c8c1", "#d6b06b", "#123b52", "#22c55e", "#ef4444", "#0c2438"];
const FREQ_OPTS = [
    { value: "once", label: "Sadece bir kez" },
    { value: "every_visit", label: "Her ziyarette" },
    { value: "every_week", label: "Haftada bir" },
];
const LANGS = [
    { code: "tr", name: "Türkçe" },
    { code: "en", name: "English" },
    { code: "de", name: "Deutsch" },
];

export default function AdminNewsletter() {
    const [subscribers, setSubscribers] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [source, setSource] = useState("");
    const [active, setActive] = useState("");
    const [settings, setSettings] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.q = search;
            if (source) params.source = source;
            if (active) params.is_active = active === "true";
            const [subRes, statRes, setRes] = await Promise.all([
                api.get("/admin/newsletter/subscribers", { params }),
                api.get("/admin/newsletter/stats"),
                api.get("/admin/newsletter/settings"),
            ]);
            setSubscribers(subRes.data.items || []);
            setStats(statRes.data);
            setSettings(setRes.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, source, active]);

    const handleDelete = async (id) => {
        if (!window.confirm("Abone silinsin mi?")) return;
        try {
            await api.delete(`/admin/newsletter/subscribers/${id}`);
            setSubscribers((p) => p.filter((s) => s.id !== id));
            toast.success("Silindi");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const handleExport = () => {
        const URL = (typeof window !== "undefined" && window.location.origin) || "";
        window.open(`${URL}/api/admin/newsletter/export-csv`, "_blank");
    };

    const updateSetting = (k, v) => setSettings((p) => ({ ...p, [k]: v }));
    const updatePopupText = (lang, v) =>
        setSettings((p) => ({ ...p, popup_text: { ...(p?.popup_text || { tr: "", en: "", de: "" }), [lang]: v } }));

    const saveSettings = async () => {
        setSavingSettings(true);
        try {
            const { data } = await api.put("/admin/newsletter/settings", {
                popup_delay: parseInt(settings.popup_delay) || 30,
                popup_text: settings.popup_text || { tr: "", en: "", de: "" },
                popup_active: !!settings.popup_active,
                display_frequency: settings.display_frequency || "once",
            });
            setSettings(data);
            toast.success("Popup ayarları kaydedildi");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-newsletter">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy">Newsletter Yönetimi</h1>
                    <p className="text-sm text-deep-blue/70">Aboneler, istatistikler ve popup ayarları.</p>
                </div>
                <button
                    onClick={handleExport}
                    className="bg-dark-navy hover:bg-deep-blue text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2"
                    data-testid="newsletter-export-btn"
                >
                    <Download className="w-4 h-4" /> CSV İndir
                </button>
            </div>

            {/* Stat cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard Icon={Users} label="Toplam Abone" value={stats.total} />
                    <StatCard Icon={Mail} label="Aktif" value={stats.active} />
                    <StatCard Icon={Calendar} label="Bu Hafta" value={stats.last_week} accent="teal" />
                    <StatCard Icon={TrendingUp} label="Bu Ay" value={stats.last_month} accent="gold" />
                </div>
            )}

            {/* Charts */}
            {stats && (
                <div className="grid lg:grid-cols-3 gap-6">
                    <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5 lg:col-span-2">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">Abone Trendi (30 gün)</h3>
                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={stats.trend || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#64748b" />
                                    <YAxis tick={{ fontSize: 10 }} stroke="#64748b" />
                                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                                    <Line type="monotone" dataKey="count" stroke="#39c8c1" strokeWidth={2} dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">Kaynak Dağılımı</h3>
                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={stats.sources || []} dataKey="count" nameKey="source" innerRadius={36} outerRadius={70} paddingAngle={2}>
                                        {(stats.sources || []).map((_, i) => (
                                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                            {(stats.sources || []).map((s, i) => (
                                <div key={s.source} className="flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                                    <span className="text-deep-blue/80">{s.source}</span>
                                    <span className="ml-auto font-data text-dark-navy font-semibold">{s.count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Filters + Subscriber list */}
            <div className="bg-white rounded-lg border border-slate-100 shadow-md p-4 flex flex-wrap items-center gap-3">
                <input
                    placeholder="E-posta ara..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="text-sm rounded-md border border-slate-300 px-3 py-2 flex-1 min-w-[180px]"
                    data-testid="newsletter-search"
                />
                <select value={source} onChange={(e) => setSource(e.target.value)} className="text-sm rounded-md border border-slate-300 px-2 py-2" data-testid="newsletter-filter-source">
                    <option value="">Tüm kaynaklar</option>
                    {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={active} onChange={(e) => setActive(e.target.value)} className="text-sm rounded-md border border-slate-300 px-2 py-2">
                    <option value="">Tümü</option>
                    <option value="true">Aktif</option>
                    <option value="false">Pasif</option>
                </select>
            </div>

            <div className="bg-white rounded-lg border border-slate-100 shadow-md overflow-hidden">
                <table className="w-full text-sm" data-testid="newsletter-subscribers-table">
                    <thead className="bg-light-bg text-deep-blue">
                        <tr>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">E-posta</th>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Kayıt</th>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Kaynak</th>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Durum</th>
                            <th className="px-4 py-2.5 w-20"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Yükleniyor...</td></tr>
                        ) : subscribers.length === 0 ? (
                            <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Abone yok.</td></tr>
                        ) : subscribers.map((s) => (
                            <tr key={s.id} className="hover:bg-light-bg/50">
                                <td className="px-4 py-2 font-medium text-dark-navy">{s.email}</td>
                                <td className="px-4 py-2 text-xs text-deep-blue/60 font-data">{s.registered_at ? new Date(s.registered_at).toLocaleString("tr-TR") : "—"}</td>
                                <td className="px-4 py-2"><span className="text-xs px-2 py-0.5 rounded bg-teal/10 text-teal-700 font-semibold">{s.source}</span></td>
                                <td className="px-4 py-2">
                                    <span className={`text-xs px-2 py-0.5 rounded ${s.is_active ? "bg-data-green/10 text-data-green" : "bg-slate-200 text-slate-500"}`}>
                                        {s.is_active ? "Aktif" : "Pasif"}
                                    </span>
                                </td>
                                <td className="px-4 py-2 text-right">
                                    <button onClick={() => handleDelete(s.id)} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded text-data-red hover:bg-red-50">
                                        <Trash2 className="w-3 h-3" /> Sil
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Popup Settings */}
            {settings && (
                <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5 space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue">Popup Ayarları</h2>
                        <button onClick={saveSettings} disabled={savingSettings} className="bg-dark-navy text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-70" data-testid="newsletter-settings-save">
                            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            Kaydet
                        </button>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <Field label="Gecikme (saniye)">
                            <input type="number" value={settings.popup_delay || 30} onChange={(e) => updateSetting("popup_delay", e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none font-data" data-testid="newsletter-popup-delay" />
                        </Field>
                        <Field label="Gösterim Sıklığı">
                            <select value={settings.display_frequency || "once"} onChange={(e) => updateSetting("display_frequency", e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none bg-white">
                                {FREQ_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </Field>
                        <div className="flex items-center justify-between bg-light-bg rounded-md px-4 py-3">
                            <span className="text-sm font-semibold text-dark-navy">Popup Aktif</span>
                            <Switch checked={!!settings.popup_active} onCheckedChange={(v) => updateSetting("popup_active", v)} data-testid="newsletter-popup-active" />
                        </div>
                    </div>
                    <Tabs defaultValue="tr">
                        <TabsList className="bg-light-bg">
                            {LANGS.map((l) => (
                                <TabsTrigger key={l.code} value={l.code} className="data-[state=active]:bg-dark-navy data-[state=active]:text-white">
                                    {l.name}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                        {LANGS.map((l) => (
                            <TabsContent key={l.code} value={l.code} className="pt-3">
                                <Field label={`Popup Metni (${l.code.toUpperCase()})`}>
                                    <textarea
                                        rows={2}
                                        value={settings.popup_text?.[l.code] || ""}
                                        onChange={(e) => updatePopupText(l.code, e.target.value)}
                                        className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-teal"
                                    />
                                </Field>
                            </TabsContent>
                        ))}
                    </Tabs>
                </div>
            )}
        </div>
    );
}

function StatCard({ Icon, label, value, accent = "deep-blue" }) {
    const accentClass = accent === "teal" ? "bg-teal/15 text-teal-700" : accent === "gold" ? "bg-gold/15 text-gold-700" : "bg-deep-blue/10 text-deep-blue";
    return (
        <div className="lift-card bg-white rounded-lg shadow-md hover:shadow-xl border border-slate-100 p-4">
            <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-deep-blue/70 font-semibold">{label}</span>
                <span className={`w-8 h-8 rounded-md inline-flex items-center justify-center ${accentClass}`}>
                    <Icon className="w-4 h-4" />
                </span>
            </div>
            <div className="mt-2 font-data text-3xl font-bold text-dark-navy">{(value || 0).toLocaleString("tr-TR")}</div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider">{label}</span>
            <div className="mt-1.5">{children}</div>
        </label>
    );
}
